import { useContext, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useForm } from 'react-hook-form';
import { ComplianceRequestContext, ConfigContext } from '@/context';
import { api } from '@/utils/api';
import { handleError } from '@/utils/error';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/Form';
import { Input } from '@/components/ui/Input';
import { useError } from '@/components/ui/useError';
import { Alert } from '@/components/ui/Alert';
import { Warning } from '@phosphor-icons/react';
import { object, pipe, string, regex, InferInput } from 'valibot';
import { valibotResolver } from '@hookform/resolvers/valibot';

type ComplianceRequestVerifyProps = {
  onCancel: (value: boolean) => void;
  onSubmit: () => void;
};

export function ComplianceRequestVerify({
  onCancel,
  onSubmit,
}: ComplianceRequestVerifyProps) {
  const [loading, setLoading] = useState(false);
  const { complianceRequest, setComplianceRequest } = useContext(
    ComplianceRequestContext,
  );
  const {
    consentSettingsCancelButton,
    consentSettingsSubmitButton,
    errorTitle,
    getComplianceRequestTitle,
    privacyRequestsVerificationCodesPlaceholder,
    privacyRequestsVerificationCodesText,
    privacyRequestsVerificationCodesTitle,
    privacyRequestsVerificationCodesRequirements,
    privacyRequestsVerificationCodesRequired,
    privacyRequestsVerificationCodesInvalid,
  } = useContext(ConfigContext);

  const { hasError, showError, hideError } = useError();

  if (!complianceRequest || !complianceRequest?.id) {
    return null;
  }
  const complianceRequestId = complianceRequest?.id;

  const formSchema = object({
    verificationCode: pipe(
      string(privacyRequestsVerificationCodesRequired),
      regex(/^[a-z\d]+$/i, privacyRequestsVerificationCodesRequirements),
    ),
  });

  const form = useForm<InferInput<typeof formSchema>>({
    resolver: valibotResolver(formSchema),
    defaultValues: {
      verificationCode: '',
    },
  });

  async function verifyComplianceRequest(
    values: InferInput<typeof formSchema>,
  ) {
    try {
      setLoading(true);
      await api.compliance.verifyComplianceRequest(
        complianceRequestId,
        values.verificationCode,
      );
      hideError();
      setComplianceRequest(null);
      onSubmit();
    } catch (err) {
      handleError(err);
      showError();
      setLoading(false);
    }
  }

  const title = getComplianceRequestTitle(complianceRequest);
  return (
    <Card title={title}>
      {hasError && (
        <Alert
          icon={<Warning size={18} weight="fill" />}
          subTitle={privacyRequestsVerificationCodesInvalid}
          title={errorTitle}
          variant="error"
        />
      )}
      <Form {...form}>
        <form
          className="space-y-5"
          onSubmit={form.handleSubmit(verifyComplianceRequest)}
        >
          <h2 className="text-lg font-medium">
            {privacyRequestsVerificationCodesTitle}
          </h2>
          <p className="text-muted-foreground">
            {privacyRequestsVerificationCodesText}
          </p>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <FormField
              control={form.control}
              name="verificationCode"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder={privacyRequestsVerificationCodesPlaceholder}
                      {...field}
                      data-testid="cd-verification-code"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex gap-2">
            <div className="flex-grow" />
            <div className="flex gap-2">
              <Button
                data-testid="cd-cancel-button"
                variant="outline"
                onClick={() => onCancel(true)}
              >
                {consentSettingsCancelButton}
              </Button>
              <Button
                data-testid="cd-submit-button"
                loading={loading}
                type="submit"
                variant="primary"
              >
                {consentSettingsSubmitButton}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </Card>
  );
}
